import { useCallback, useEffect, useState } from 'react'

import { Delete } from '@mui/icons-material'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material'
import { AxiosError } from 'axios'
import { Button, useNotify, useTranslate } from 'react-admin'

import { Center, Divider, Progress, Stack, Text } from '@mantine/core'
import {
  useDeleteApiPrepaymentsId,
  useGetApiPrepaymentsIdRegenerationImpact,
} from '../../codegen/generated/prepayments'
import { useGetApiStatementsJobStatusJobId } from '../../codegen/generated/statements'
import { API } from '../../helpers/Api.helper'
import { Prepayment } from '../../types/Prepayments'
import { PrepaymentStatementsUpdate } from './PrepaymentStatementsUpdate'

type DeletePrepaymentProps = {
  prepaymentId: Prepayment['id']
  prepaymentLabel: Prepayment['label']
  onDelete: () => void
}

export const DeletePrepayment = ({
  prepaymentId,
  prepaymentLabel,
  onDelete,
}: DeletePrepaymentProps): JSX.Element => {
  const translate = useTranslate()
  const notify = useNotify()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [jobId, setJobId] = useState<string | undefined>()

  const prepaymentRegenerationImpactQuery = useGetApiPrepaymentsIdRegenerationImpact(
    prepaymentId,
    {},
    {
      query: { enabled: isModalOpen },
    },
  )
  const statementsJobStatusQuery = useGetApiStatementsJobStatusJobId(jobId ?? '', {
    query: {
      enabled: !!jobId,
      refetchInterval: data =>
        !((data?.body.job as any)?.progress === 100 && (data?.body.job as any)?.finishedOn)
          ? 2000
          : false,
    },
  })
  const prepaymentDeleteMutation = useDeleteApiPrepaymentsId()

  const finaliseDeletePrepayment = useCallback(() => {
    notify(
      translate('pages.prepayments.deletePrepaymentModal.success', {
        label: prepaymentLabel,
      }),
      { type: 'success' },
    )

    onDelete()
    setIsLoading(false)
    setIsModalOpen(false)
    setJobId(undefined)
  }, [notify, onDelete, prepaymentLabel, translate])

  const onDeletePrepayment = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await prepaymentDeleteMutation.mutateAsync({ id: prepaymentId })

      if (res.body.jobId) {
        setJobId(res.body.jobId)
      } else {
        finaliseDeletePrepayment()
      }
    } catch (e: unknown) {
      const message =
        (e as AxiosError)?.response?.data.message || (e as AxiosError)?.response?.data || e

      notify(`${translate('pages.prepayments.deletePrepaymentModal.error')} - ${message}`, {
        type: 'error',
      })
      console.error('Error - delete prepayment', e)
    }
  }, [
    prepaymentDeleteMutation.mutateAsync,
    prepaymentId,
    prepaymentLabel,
    notify,
    translate,
    onDelete,
  ])

  useEffect(() => {
    if (
      (statementsJobStatusQuery.data?.body.job as any)?.progress === 100 &&
      (statementsJobStatusQuery.data?.body.job as any)?.finishedOn
    ) {
      finaliseDeletePrepayment()
    }
  }, [statementsJobStatusQuery.data?.body.job, finaliseDeletePrepayment])

  return (
    <>
      <Tooltip title={translate('_generics.delete')} placement="top" arrow>
        <span>
          <Button
            size="small"
            color="error"
            onClick={() => setIsModalOpen(true)}
            style={{
              minWidth: 'fit-content',
            }}
          >
            <Delete
              color="error"
              fontSize="small"
              style={{
                marginLeft: '0.5rem',
              }}
            />
          </Button>
        </span>
      </Tooltip>
      <Dialog
        // aria-labelledby="prepayments-delete"
        open={isModalOpen}
        maxWidth="xs"
        fullWidth
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle id="prepayments-delete">
          {translate('pages.prepayments.deletePrepaymentModal.title')}
        </DialogTitle>
        <DialogContent dividers>
          {typeof jobId !== 'string' ? (
            <>
              <Typography variant="body1" color="textPrimary">
                {translate('pages.prepayments.deletePrepaymentModal.description', {
                  label: prepaymentLabel,
                })}
              </Typography>
              {((prepaymentRegenerationImpactQuery.data?.body.statements ?? []).length > 0 ||
                prepaymentRegenerationImpactQuery.isLoading) && <Divider my="xl" w="100%" />}
              <PrepaymentStatementsUpdate
                statements={prepaymentRegenerationImpactQuery.data?.body.statements ?? []}
                loading={prepaymentRegenerationImpactQuery.isLoading}
              />
            </>
          ) : (
            <Stack gap="xs">
              <Progress value={75} striped animated />
              <Center>
                <Text size="sm" fs="italic">
                  {translate('pages.prepayments.xStatementsBeingRegen', {
                    count: prepaymentRegenerationImpactQuery.data?.body.statements.length ?? 0,
                  })}
                </Text>
              </Center>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsModalOpen(false)
            }}
            label={translate('_generics.cancel')}
          />
          <Button
            variant="contained"
            color="error"
            onClick={onDeletePrepayment}
            label={translate('_generics.delete')}
            disabled={isLoading}
          >
            <Delete />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
